import { ITopicImportStats } from "@/api";
import { Button } from "@/components/shared/buttons/Button";
import { Table } from "@/components/shared/table/Table";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import WarningIcon from "@mui/icons-material/Warning";
import { Stack, Typography } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { useState } from "react";
import { SkippedRowsList } from "./SkippedRowsList";

interface IProps {
    results: ITopicImportStats;
}

/**
 * PreprocessResult component - Displays import preview statistics
 */
export const PreprocessResult = ({ results }: IProps) => {
    const [showSkippedDetails, setShowSkippedDetails] = useState(false);

    const getStatsInfo = (key: string) => {
        switch (key) {
            case "topics_created":
                return {
                    label: "Topics to Create",
                    description: "New topics will be created",
                    warning: false,
                };
            case "clause_associations_added":
                return {
                    label: "Clause Associations to Add",
                    description:
                        "New clause-topic associations will be created",
                    warning: false,
                };
            case "clause_associations_removed":
                return {
                    label: "Clause Associations to Remove",
                    description:
                        "Existing clause-topic associations will be removed",
                    warning: true,
                };
            case "field_associations_added":
                return {
                    label: "Field Associations to Add",
                    description: "New field-topic associations will be created",
                    warning: false,
                };
            case "field_associations_removed":
                return {
                    label: "Field Associations to Remove",
                    description:
                        "Existing field-topic associations will be removed",
                    warning: true,
                };
            case "skipped_rows":
                return {
                    label: "Skipped Rows",
                    value: results.skipped_rows.length,
                    description: "Rows that will be skipped due to errors",
                    warning: false,
                    showDetails: true,
                };
            default:
                return {
                    label: key,
                    description: "",
                    warning: false,
                };
        }
    };

    // Transform stats object to rows for DataGrid
    const rows = Object.entries(results).map(([key, value], index) => ({
        id: index,
        key,
        value: Array.isArray(value) ? value.length : value,
        ...getStatsInfo(key),
    }));

    const columns: GridColDef[] = [
        {
            field: "label",
            headerName: "Type",
            flex: 1,
            minWidth: 200,
            sortable: false,
        },
        {
            field: "value",
            headerName: "Count",
            minWidth: 120,
            align: "center",
            headerAlign: "center",
            sortable: false,
        },
        {
            field: "description",
            headerName: "",
            flex: 2,
            minWidth: 300,
            renderCell: (params: GridRenderCellParams) => {
                const { warning, description, key, showDetails, value } =
                    params.row;

                if (warning && value > 0) {
                    return (
                        <Stack direction="row" alignItems="center" gap={1}>
                            <WarningIcon color="warning" fontSize="small" />
                            <Typography
                                fontWeight="bold"
                                color="warning.main"
                                variant="body2"
                            >
                                Warning: Associations will be removed
                            </Typography>
                        </Stack>
                    );
                }

                if (key === "skipped_rows" && showDetails) {
                    return (
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Typography variant="body2">
                                {description}
                            </Typography>

                            {value > 0 && (
                                <Button
                                    variant="text"
                                    size="small"
                                    type="button"
                                    color="inherit"
                                    startIcon={
                                        showSkippedDetails ? (
                                            <KeyboardArrowUpIcon />
                                        ) : (
                                            <KeyboardArrowDownIcon />
                                        )
                                    }
                                    onClick={() =>
                                        setShowSkippedDetails(
                                            !showSkippedDetails
                                        )
                                    }
                                >
                                    {showSkippedDetails
                                        ? "Hide Details"
                                        : "Show Details"}
                                </Button>
                            )}
                        </Stack>
                    );
                }

                return description;
            },
        },
    ];

    return (
        <Stack width={"100%"} gap={2}>
            <Table
                rows={rows}
                columns={columns}
                disableColumnMenu
                hideFooter
                disableColumnFilter
                disableColumnSorting
            />
            {showSkippedDetails && (
                <SkippedRowsList skippedRows={results.skipped_rows} />
            )}
        </Stack>
    );
};
